import { Tree } from '../types'

/**
 * Takes an array of CategorySearchResults and converts it into an array of Trees
 */
export function categorySearchResultsToTrees(searchResults: CategorySearchResult[]) {
	return searchResults.map((result) => result.node)
}

/**
 * Merges two tree nodes by combining their children.
 *
 * @param node1 The first tree node
 * @param node2 The second tree node
 * @returns The merged tree node
 */
export function mergeNodes(node1: Tree, node2: SearchResultNode): Tree {
	const mergedChildren = mergeTrees(node1.children, node2.children)
	return {
		id: node1.id,
		name: node1.name, // Assume names are the same for matching IDs
		plid: node1.plid,
		children: mergedChildren,
	}
}

/**
 * Accepts two arrays of trees and merges them together
 *
 * @param treeArray1 Array of Trees
 * @param treeArray2 Array of Trees
 * @returns Array of merged trees
 */
export function mergeTrees(treeArray1: Tree[], treeArray2: SearchResultNode[]): Tree[] {
	const map = new Map<number, Tree>()

	// Add all nodes from the first array to map
	treeArray1.forEach((node) => {
		map.set(node.id as number, node)
	})

	// Merge or add nodes from the second array to map
	treeArray2.forEach((node) => {
		if (map.has(node.id as number)) {
			// Merge nodes if IDs match
			const existingNode = map.get(node.id as number)
			if (existingNode) {
				map.set(node.id as number, mergeNodes(existingNode, node))
			}
		} else {
			// Add the new node
			map.set(node.id as number, node as Tree)
		}
	})

	// Return the merged result as an array
	return Array.from(map.values())
}

/**
 * Removes a node and it's children from an array of Trees
 *
 * @param treeArray Array of Trees
 * @param idToRemove The ID of the node to remove from the array of Trees
 * @returns New Array of Trees
 */
export function removeNodeFromTree(treeArray: Tree[], idToRemove: number): Tree[] {
	// Recursive function for removing specified Node
	const removeNode = (nodes: Tree[]): Tree[] =>
		nodes
			.filter((node) => node.id !== idToRemove) // Exclude the node with the target ID
			.map((node) => ({
				...node,
				children: removeNode(node.children), // Recursively process children
			}))

	return removeNode(treeArray)
}

/**
 * Accepts an array of trees and returns all IDs of nodes within those trees
 * @param treeArray Array of Trees
 * @returns Array of IDs
 */
export function getAllIds(treeArray: Tree[]): number[] {
	// Recursive function for collecting IDs
	const collectIds = (nodes: Tree[], ids: number[] = []): number[] => {
		nodes.forEach((node) => {
			ids.push(node.id as number) // Add the current node's id
			collectIds(node.children, ids) // Recursively collect ids from children
		})
		return ids
	}

	return collectIds(treeArray)
}
